import {
    getType,
    toDate,
    saveFilters,
    loadFilters,
} from './helpers.js'
import SimpleSearchMixin from './SimpleSearchMixin.js'
import { createRemoteMixin } from './RemoteSearchMixin.js'

export function mixinsFactory(settings = null) {
    if (settings === null) {
        // simple default mixin
        return SimpleSearchMixin;
    }

    if (typeof settings.url === 'string') {
        // settings.url present -> fetch/filter/sort/paginate via a QueryBuilder
        // REST endpoint instead of filtering an already-loaded rows array
        return createRemoteMixin(settings);
    }

    const defaultSearchArgs = {};
    const filters = {}
    const customFilters = {}
    const filterNames = Object.keys(settings);

    for (let key of filterNames) {
        const filter = settings[key];
        
        switch(filter) {
            case Number:
                defaultSearchArgs[key] = null;
                /**
                 * Try to assure that values are valid numbers before matching
                 * 
                 * @param {String|Number} search 
                 * @param {String|Number} data 
                 * @returns {Boolean}
                 */
                filters[key] = (search, data) => {
                    const searchVal = -(-search);
                    const numVal = -(-data);

                    if (isNaN(numVal) || isNaN(searchVal)) {
                        return false;
                    }

                    return searchVal === numVal;
                }
            break;
            case String:
                defaultSearchArgs[key] = '';
                /**
                 * String filtering function 
                 * 
                 * @param {String} search 
                 * @param {String} data 
                 * @returns {Boolean}
                 */
                filters[key] = (search, data) => {
                    if (getType(data) !== String) return false;
                    
                    const searchArgs = search.trim().toLowerCase().split(',');

                    for (let search of searchArgs) {
                        if (search.trim().length === 0) {
                            continue;
                        }
                        if (data.toLowerCase().includes(search.trim())) {
                            return true;
                        }
                    }

                    return false;
                }
            break;
            case Date:
                defaultSearchArgs[key] = '';
                /**
                 * Trying to compare date strings by creating Date objects
                 * 
                 * @param {String} search 
                 * @param {String} data 
                 * @returns {Boolean}
                 */
                filters[key] = (search, data) => {
                    const searchVal = toDate(search);
                    const dateVal = toDate(data);
                    // invalid search
                    if (searchVal === null) {
                        return true;
                    }
                    // data might be null or not a date so discard the row
                    if (dateVal === null) {
                        return false;
                    }

                    if (searchVal.getTime() != dateVal.getTime()) {
                        return false;
                    }

                    return true;
                }
            break;
            // special case to use window.stringMatches function
            case 'stringMatches':
                defaultSearchArgs[key] = '';
                /**
                 * String filtering using the window.stringMatches function
                 * 
                 * @param {String} search 
                 * @param {String} data 
                 * @returns {Boolean}
                 */
                filters[key] = (search, data) => {
                    if (getType(data) !== String) return false;
                    
                    return window.stringMatches(search, data);
                }
            break;
            case 'daterange':
                /**
                 * @typedef DateRange
                 * @prop {String} start YYYY-MM-DD
                 * @prop {String} end YYYY-MM-DD
                 */
                /**
                 * @type {DateRange}
                 */
                defaultSearchArgs[key] = {
                    start: '',
                    end: '',
                };
                /**
                 * Trying to compare date strings by creating Date objects
                 * 
                 * @param {DateRange} search 
                 * @param {String} data 
                 * @returns {Boolean}
                 */
                filters[key] = (search, data) => {
                    const searchStart = toDate(search.start)
                    const searchEnd = toDate(search.end, '23:59:59')
                    const dateVal = toDate(data, null)
                    // invalid search
                    if (searchStart === null && searchEnd === null) {
                        return true;
                    }
                    // if date is before the start, it is not included
                    if (searchStart !== null && searchStart > dateVal) {
                        return false;
                    }
                    // if date is after the end, it is not included
                    if (searchEnd !== null && searchEnd < dateVal) {
                        return false;
                    }

                    return true;
                }
            break;
            default:
                if (getType(filter) === Function) {
                    customFilters[key] = filter;
                    defaultSearchArgs[key] = '';
                }
        }
    }
    
    return {
        mixins: [ SimpleSearchMixin ],
        // namespace/rows/currentSortDir/currentSort already come from SimpleSearchMixin (mixed in
        // above) - redeclaring them here too caused the same data key to be registered by two
        // nested mixin layers, which made currentSortDir/currentSort resolve inconsistently
        // (setSort() would toggle one copy while sortedRows/the template read another).
        data: () => ({
            searchArgs: defaultSearchArgs,
        }),
        computed: {
            filteredRows() {
                let rows = this.rows;

                if (this.preFilteredRows) {
                    rows = this.preFilteredRows;
                }

                const searchArgs = this.searchArgs;
    
                return rows.filter(row => {
                    for (let key of filterNames) {
                        // if search argument or value on this key is empty, just skip
                        if (!searchArgs[key]) continue;
                        if (row[key] === null) return false;
    
                        const searchArg = searchArgs[key]
                        const value = row[key];
    
                        let result = true;
    
                        if (customFilters[key]) {
                            const filterFunc = customFilters[key];
    
                            result = filterFunc(searchArg, row);
                        } else {
                            const filterFunc = filters[key];
    
                            result = filterFunc(searchArg, value);
                        }
    
                        if (result === false) {
                            return false;
                        }
                    }
    
                    return true;
                });
            },
        },
        watch: {
            searchArgs: {
                deep: true,
                immediate: true,
                handler(val, oldVal) {
                    // first run, no need to save
                    if (!oldVal) return;
                    
                    if (val && this.namespace) {
                        const saved = { ...val }

                        for (const skip of this.persistSkip) delete saved[skip]

                        saveFilters(this.namespace, saved, this.stateStore())
                    }
                }
            }
        },
        methods: {
            onSearchChange(evt, name) {
                this.searchArgs[name] = evt.target.value;
            },
        },
        beforeMount() {
            if (this.namespace && this.searchArgs) {
                if (loadFilters(this.namespace, this.searchArgs, this.stateStore(), this.persistSkip) === true) {
                    this.stateRestored = true
                }
            }
        },
    }
}

export function mixinsHelp(mixin = null) {
    // provide specific mixin help when mixin is not null
    console.log(`
The simple default mixin will provide these:

- data
    - rows (for raw data)
    - search (simple string for search, comma separated arguments work)
    - currentSortDir (asc/desc)
    - currentSort (default empty string, use a property name in your data)
- computed
    - filteredRows (this filters the raw data from rows)
    - sortedRows (this sorts the filteredRows data)
- methods
    - setSort(key, forceSort = null) (key = property name in your data, forceSort asc/desc)
- autosave and load search filters by setting window.$_namespace as a string key

All can be overwritten.

The computed property "sortedRows" is what you want to use in a datatable in most cases. Note that you can use this mixin in whatever v-for you have!

Pass a "url" key in the settings object (alongside your field definitions) to switch
to remote mode: filtering/sorting/paging happen on the server via a QueryBuilder REST
endpoint instead of in the browser. Also required: a "primaryKey" key (the $allowed
field name used for QueryBuilder's ?count= operator) - if the endpoint's response
doesn't already include totalCount, a second filters-only request fetches it
automatically. Only String, Number, Date, and 'stringMatches' field types are
supported in remote mode. Fetched pages are cached in-memory (LRU, "cacheSize"
settings key, default 20) and filter changes are debounced ("debounceMs" settings
key, default 300). See references/tables-and-pagination.md in the vue-common-components
skill for the full contract and required <wn-table-pages> template wiring
(ref="tablePages", v-bind for totalCount, v-on for pageChange - NOT individual
:totalCount=/@pageChange= attributes, which silently break in apps/cloud's in-DOM
templates).
`)
}